import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, Loader2, Save, Stamp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';
import CampusRoleShell from '@/components/CampusRoleShell';
import ReferralStatusBadge from '../components/ReferralStatusBadge';
import ReferralLetterEditor from '../components/ReferralLetterEditor';
import SignatureCaptureModal from '../components/SignatureCaptureModal';
import IssuedLetterViewer from '../components/IssuedLetterViewer';
import LetterRequestSidebar from '../components/LetterRequestSidebar';
import { referralPortalApi } from '../api/referralPortal.api';
import { toast } from 'sonner';

export default function DoctorReferralLetterPage() {
  const { id } = useParams();
  const { tenant } = useTenant();
  const primary = tenant?.branding?.primary_color || '#F15A22';
  const [row, setRow] = useState(null);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [savedSig, setSavedSig] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await referralPortalApi.inboxOne(id);
      setRow(data);
      setSections(data.letter?.sections || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureDraft = async () => {
    if (row?.letter?.sections) return row;
    const data = await referralPortalApi.startReview(id);
    setRow(data);
    setSections(data.letter?.sections || {});
    return data;
  };

  const save = async () => {
    setBusy(true);
    try {
      await ensureDraft();
      setRow(await referralPortalApi.saveLetter(id, sections));
      toast.success('Letter draft saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const beginIssue = async () => {
    setBusy(true);
    try {
      await ensureDraft();
      await referralPortalApi.saveLetter(id, sections);
      const me = await referralPortalApi.getMySignature();
      setSavedSig(row?.letter?.signature_image || me.signature_image || '');
      setSigOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not prepare issue');
    } finally {
      setBusy(false);
    }
  };

  const confirmIssue = async ({ signatureImage, saveToProfile }) => {
    setBusy(true);
    try {
      if (saveToProfile) await referralPortalApi.saveMySignature(signatureImage);
      await referralPortalApi.saveLetter(id, sections, signatureImage);
      setRow(await referralPortalApi.issueLetter(id));
      setSigOpen(false);
      toast.success('Referral letter issued with signature');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Issue failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !row) {
    return (
      <CampusRoleShell pageTitle="Referral letter">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      </CampusRoleShell>
    );
  }

  const canEdit = !['issued', 'rejected', 'cancelled', 'pending_fic'].includes(row.status);
  const actions = (
    <div className="flex flex-wrap gap-2">
      <a href={referralPortalApi.doctorLetterPreviewUrl(id)} target="_blank" rel="noreferrer"
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 text-sm">
        <ExternalLink className="h-4 w-4" /> Preview
      </a>
      <a href={referralPortalApi.doctorLetterPdfUrl(id)} target="_blank" rel="noreferrer"
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-200 bg-white px-3 text-sm">
        <Download className="h-4 w-4" /> PDF
      </a>
      {canEdit && (
        <>
          <Button disabled={busy} variant="outline" onClick={save}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save draft
          </Button>
          <Button disabled={busy} className="text-white" style={{ background: primary }} onClick={beginIssue}>
            <Stamp className="mr-1 h-4 w-4" /> Issue letter
          </Button>
        </>
      )}
    </div>
  );

  return (
    <CampusRoleShell pageTitle={row.request_number} pageSubtitle="Referral letter" rightActions={actions}>
      <Link to="/doctor/referrals" className="mb-3 inline-flex items-center gap-1 text-xs text-[#5A6B7C]">
        <ArrowLeft className="h-3.5 w-3.5" /> Inbox
        <ReferralStatusBadge status={row.status} />
      </Link>
      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-7">
          {canEdit ? (
            <ReferralLetterEditor sections={sections} onChange={setSections} />
          ) : row.status === 'issued' ? (
            <IssuedLetterViewer
              previewUrl={referralPortalApi.doctorLetterPreviewUrl(id)}
              title={`${row.request_number} · branded letter`}
            />
          ) : (
            <p className="text-sm text-stone-500">Letter is {row.status}. Editing is locked.</p>
          )}
        </div>
        <LetterRequestSidebar
          row={row}
          canEdit={canEdit}
          primary={primary}
          busy={busy}
          setBusy={setBusy}
          ensureDraft={ensureDraft}
          signatureSrc={row.letter?.signature_image || savedSig || ''}
          issued={row.status === 'issued'}
        />
      </div>
      <SignatureCaptureModal
        open={sigOpen}
        onOpenChange={setSigOpen}
        savedImage={savedSig || row.letter?.signature_image || ''}
        doctorName={sections.doctor_signature_name || ''}
        busy={busy}
        onConfirm={confirmIssue}
      />
    </CampusRoleShell>
  );
}
